import api from './api';

export interface MeetingText {
  id: number;
  name?: string | null;
  title: string;
  content: string;
  text_type: 'transcript' | 'rt_transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other' | 'summary_section' | 'agenda';
  is_ai_generated: boolean;
  confidence_score: number | null;
  word_count: number | null;
  meeting_id: number;
  meeting: {
    id: number;
    title: string;
    scheduled_at: string;
  };
  // Null for AI-generated texts: the agent pipeline creates summaries, agendas and
  // questionnaire results with no user behind them (see routers/agent.py), and the
  // API schema types this as `UserInfo | None`.
  created_by: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  updated_by: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface MeetingTextCreate {
  name?: string;
  title: string;
  content: string;
  text_type: 'transcript' | 'rt_transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other' | 'summary_section' | 'agenda';
  is_ai_generated?: boolean;
  confidence_score?: number;
  meeting_id: number;
}

export interface MeetingTextUpdate {
  name?: string;
  title?: string;
  content?: string;
  text_type?: 'transcript' | 'rt_transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other' | 'summary_section' | 'agenda';
  confidence_score?: number;
}

export interface TranscriptionJobResponse {
  job_id: number;
  job_status: string;
  message: string;
  meeting_id: number;
}

export const meetingTextService = {
  async getMeetingTexts(
    meetingId: number,
    textType?: 'transcript' | 'rt_transcript' | 'summary' | 'notes' | 'treatment_plan' | 'homework' | 'other' | 'summary_section' | 'agenda',
  ): Promise<MeetingText[]> {
    const params = new URLSearchParams({ meeting_id: meetingId.toString() });
    if (textType) params.append('text_type', textType);

    const response = await api.get(`/meeting-texts/?${params}`);
    return response.data;
  },

  async getMeetingText(textId: number): Promise<MeetingText> {
    const response = await api.get(`/meeting-texts/${textId}`);
    return response.data;
  },

  async createMeetingText(textData: MeetingTextCreate): Promise<MeetingText> {
    const response = await api.post('/meeting-texts/', textData);
    return response.data;
  },

  async updateMeetingText(textId: number, textData: MeetingTextUpdate): Promise<MeetingText> {
    const response = await api.put(`/meeting-texts/${textId}`, textData);
    return response.data;
  },

  async deleteMeetingText(textId: number): Promise<void> {
    await api.delete(`/meeting-texts/${textId}`);
  },

  async getSummarySectionNames(): Promise<string[]> {
    const response = await api.get('/meeting-texts/summary-section-names');
    return response.data;
  },

  async uploadTranscript(meetingId: number, file: File): Promise<MeetingText> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/meeting-texts/upload-transcript/${meetingId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async uploadRecording(meetingId: number, file: File, language?: string): Promise<TranscriptionJobResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (language) {
      formData.append('language', language);
    }

    const response = await api.post(`/meeting-texts/upload-recording/${meetingId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};